import { authFetch } from '../../api/csrf';
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext';
import { internalApprovalService } from '../../api/crm/support/internalSupportService';
import { internalApprovalCategoryService } from '../../api/crm/config/configService';
import SearchableDropdown from '../../components/common/SearchableDropdown';
import {
    Plus,
    Search,
    Filter,
    CheckCircle,
    XCircle,
    Clock,
    X,
    FileText,
    Download,
    User,
    ChevronRight,
    ArrowLeft,
    Paperclip,
    Send,
    Eye,
    Edit2,
    Check,
    MessageSquare
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import Button from '../../components/common/Button/Button';


const InternalApprovals = () => {
    const { user: currentUser } = useAuth();
    const queryClient = useQueryClient();
    const isSuperAdmin = currentUser?.role === 'super_admin';

    // Active main tab: 'inbox' or 'sent'
    const [activeTab, setActiveTab] = useState('inbox');

    // Pagination State
    const [page, setPage] = useState(1);
    const limit = 4;

    // Detail Selection
    const [selectedApprovalId, setSelectedApprovalId] = useState(null);

    // Search & Filters
    const [searchQuery, setSearchQuery] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // Today's date string (YYYY-MM-DD) — used to restrict future dates
    const todayStr = new Date().toISOString().split('T')[0];

    // Reset page to 1 when filters or tabs change
    React.useEffect(() => {
        setPage(1);
    }, [searchQuery, categoryFilter, statusFilter, activeTab, startDate, endDate]);

    // Validate date range
    React.useEffect(() => {
        if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
            toast.error("From Date cannot be after To Date");
            setEndDate('');
        }
    }, [startDate, endDate]);

    // Create Modal state
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newApprovalData, setNewApprovalData] = useState({
        title: '',
        category: '',
        description: '',
        attachments: [],
        approverIds: [],
        approverNames: []
    });

    // Form validation errors for create modal
    const [formErrors, setFormErrors] = useState({});
    const [formTouched, setFormTouched] = useState({});

    // Approver Search text in creation flow
    const [approverSearch, setApproverSearch] = useState('');

    // Review Remarks State
    const [reviewRemarks, setReviewRemarks] = useState('');

    // Queries
    // 0. Fetch dynamic categories
    const { data: categoriesResponse } = useQuery({
        queryKey: ['internalApprovalCategoriesDropdown'],
        queryFn: () => internalApprovalCategoryService.getDropdown()
    });
    const approvalCategories = categoriesResponse?.data || [];

    // Fetch Approval KPIs
    const { data: approvalKPIsResponse } = useQuery({
        queryKey: ['internalApprovalKPIs'],
        queryFn: () => internalApprovalService.getKPIs()
    });
    const approvalKPIs = approvalKPIsResponse?.data;

    // Auto-select first category when loaded or when modal is opened
    React.useEffect(() => {
        if (approvalCategories.length > 0 && !newApprovalData.category) {
            setNewApprovalData(prev => ({
                ...prev,
                category: approvalCategories[0].name
            }));
        }
    }, [approvalCategories, newApprovalData.category]);

    // 1. Fetch Inbox/Sent Requests
    const { data: approvalsResponse, isLoading } = useQuery({
        queryKey: ['internalApprovals', activeTab, searchQuery, categoryFilter, statusFilter, startDate, endDate, page],
        queryFn: () => internalApprovalService.getAll({
            type: activeTab,
            search: searchQuery,
            category: categoryFilter,
            status: statusFilter, // 'all' is forwarded as-is; backend skips filter for 'all'
            startDate,
            endDate,
            page,
            limit
        })
    });
    const approvals = approvalsResponse?.data || [];
    const totalApprovals = approvalsResponse?.total || approvals.length;
    const totalPages = Math.ceil(totalApprovals / limit);

    const handleDownloadAttachment = async (e, fileUrl, fileName) => {
        e.preventDefault();
        try {
            const response = await authFetch(fileUrl);
            if (!response.ok) {
                throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`);
            }
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileName || 'download';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            window.open(fileUrl, '_blank');
        }
    };

    // 2. Fetch selected approval details
    const { data: selectedApprovalResponse, isLoading: detailsLoading } = useQuery({
        queryKey: ['internalApprovalDetails', selectedApprovalId],
        queryFn: () => internalApprovalService.getById(selectedApprovalId),
        enabled: !!selectedApprovalId
    });
    const selectedApproval = selectedApprovalResponse?.data;

    // State for approvers pagination/search inside Modal
    const [appSearch, setAppSearch] = useState('');
    const [appPage, setAppPage] = useState(1);
    const [employeesList, setEmployeesList] = useState([]);
    const [hasMoreApp, setHasMoreApp] = useState(false);
    const [loadingApp, setLoadingApp] = useState(false);
    const [loadingMoreApp, setLoadingMoreApp] = useState(false);

    // Fetch Approvers (Modal)
    const fetchApprovers = async (pageToFetch, searchString, isLoadMore = false) => {
        try {
            if (isLoadMore) {
                setLoadingMoreApp(true);
            } else {
                setLoadingApp(true);
            }
            const params = {
                page: pageToFetch,
                limit: 20,
                search: searchString
            };
            const response = await internalApprovalService.getApprovers(params);
            const data = response?.data || response;
            const apps = data?.approvers || [];
            const pagination = data?.pagination || {};

            setEmployeesList(prev => isLoadMore ? [...prev, ...apps] : apps);
            setHasMoreApp(!!pagination.hasNextPage);
        } catch (error) {
        } finally {
            setLoadingApp(false);
            setLoadingMoreApp(false);
        }
    };

    // Fetch initial approvers when modal opens
    useEffect(() => {
        if (showCreateModal) {
            setAppSearch('');
            setAppPage(1);
            fetchApprovers(1, '');
        }
    }, [showCreateModal]);

    // Handlers for approver searches & infinite scroll
    const handleAppSearch = (term) => {
        setAppSearch(term);
        setAppPage(1);
        fetchApprovers(1, term);
    };

    const handleAppScrollToBottom = () => {
        if (hasMoreApp && !loadingApp && !loadingMoreApp) {
            const nextPage = appPage + 1;
            setAppPage(nextPage);
            fetchApprovers(nextPage, appSearch, true);
        }
    };

    // Mutations
    // 1. Create Approval Request
    const createApprovalMutation = useMutation({
        mutationFn: (data) => internalApprovalService.create(data),
        onSuccess: () => {
            toast.success('Approval request raised successfully!');
            setShowCreateModal(false);
            setNewApprovalData({
                title: '',
                category: approvalCategories[0]?.name || '',
                description: '',
                attachments: [],
                approverIds: [],
                approverNames: []
            });
            setFormErrors({});
            setFormTouched({});
            queryClient.invalidateQueries({ queryKey: ['internalApprovals'] });
            queryClient.invalidateQueries({ queryKey: ['internalApprovalKPIs'] });
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to raise approval request');
        }
    });

    // 2. Submit Review Decision
    const submitDecisionMutation = useMutation({
        mutationFn: ({ id, action, remarks }) => internalApprovalService.submitDecision(id, { action, remarks }),
        onSuccess: (data, variables) => {
            toast.success(`Request ${variables.action === 'approve' ? 'Approved' : 'Rejected'} successfully!`);
            setReviewRemarks('');
            queryClient.invalidateQueries({ queryKey: ['internalApprovalDetails', selectedApprovalId] });
            queryClient.invalidateQueries({ queryKey: ['internalApprovals'] });
            queryClient.invalidateQueries({ queryKey: ['internalApprovalKPIs'] });
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to submit decision');
        }
    });

    // Backend category filtered list
    const filteredApprovals = approvals;

    // Form validation helper
    const validateCreateForm = (data) => {
        const errors = {};
        if (!data.title.trim()) {
            errors.title = 'Subject/Title is required.';
        } else if (data.title.trim().length < 5) {
            errors.title = 'Subject must be at least 5 characters.';
        } else if (data.title.trim().length > 100) {
            errors.title = 'Subject cannot exceed 100 characters.';
        }

        if (!data.description.trim()) {
            errors.description = 'Description/Justification is required.';
        } else if (data.description.trim().length < 10) {
            errors.description = 'Description must be at least 10 characters.';
        }

        if (!data.approverIds || data.approverIds.length === 0) {
            errors.approvers = 'Please add at least one approver.';
        }
        return errors;
    };

    // Per-field blur handler for real-time validation
    const handleFieldBlur = (field) => {
        setFormTouched(prev => ({ ...prev, [field]: true }));
        const errors = validateCreateForm(newApprovalData);
        setFormErrors(errors);
    };

    // Per-field change: clear its error if now valid
    const handleFieldChange = (field, value) => {
        const updated = { ...newApprovalData, [field]: value };
        setNewApprovalData(updated);
        if (formTouched[field]) {
            const errors = validateCreateForm(updated);
            setFormErrors(errors);
        }
    };

    // Submit New Request
    const handleCreateApproval = (e) => {
        e.preventDefault();

        // Run full validation
        const errors = validateCreateForm(newApprovalData);
        setFormTouched({ title: true, description: true, approvers: true });
        setFormErrors(errors);

        if (Object.keys(errors).length > 0) {
            if (errors.approvers) {
                toast.error(errors.approvers);
            } else {
                toast.error('Please fix the validation errors before submitting.');
            }
            return;
        }

        createApprovalMutation.mutate({
            title: newApprovalData.title.trim(),
            category: newApprovalData.category,
            description: newApprovalData.description.trim(),
            approvers: newApprovalData.approverIds,
            // attachments are already real objects from Cloudinary: { fileName, fileUrl, fileSize }
            attachments: newApprovalData.attachments
        });
    };

    // Open Modal Reset Helper
    const handleOpenCreateModal = () => {
        setNewApprovalData({
            title: '',
            category: approvalCategories[0]?.name || '',
            description: '',
            attachments: [],
            approverIds: [],
            approverNames: []
        });
        setFormErrors({});
        setFormTouched({});
        setShowCreateModal(true);
    };

    // File Attach — real Cloudinary upload
    const [uploadingFiles, setUploadingFiles] = useState(false);
    const [isDragging, setIsDragging] = useState(false);

    const isAllowedFileType = (file) => {
        if (!file) return false;
        const mime = file.type || '';
        const name = (file.name || '').toLowerCase();
        if (mime.startsWith('image/') || mime.startsWith('video/') || mime.startsWith('audio/')) {
            return true;
        }
        const allowedExtensions = [
            '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.csv', '.ppt', '.pptx', '.txt', '.rtf'
        ];
        return allowedExtensions.some(ext => name.endsWith(ext));
    };

    const uploadFiles = async (files) => {
        if (!files.length) return;

        const invalidFiles = files.filter(file => !isAllowedFileType(file));
        if (invalidFiles.length > 0) {
            toast.error('Only documents, images, videos, and audio recordings are allowed.');
            return;
        }

        setUploadingFiles(true);
        try {
            const uploaded = await Promise.all(
                files.map(file => internalApprovalService.uploadAttachment(file))
            );
            setNewApprovalData(prev => ({
                ...prev,
                attachments: [
                    ...prev.attachments,
                    ...uploaded.map(u => ({ fileName: u.fileName, fileUrl: u.fileUrl, fileSize: u.fileSize }))
                ]
            }));
            toast.success(`${files.length} file(s) uploaded to cloud successfully`);
        } catch (err) {
            toast.error('File upload failed: ' + (err.message || 'Unknown error'));
        } finally {
            setUploadingFiles(false);
        }
    };

    const handleFileAttach = (e) => {
        const files = Array.from(e.target.files);
        uploadFiles(files);
        e.target.value = '';
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!uploadingFiles) {
            setIsDragging(true);
        }
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        if (!uploadingFiles) {
            const files = Array.from(e.dataTransfer.files);
            uploadFiles(files);
        }
    };

    // Check if current user can act on this approval (parallel model — any approver whose own step is still Pending)
    const isCurrentUserPendingApprover = (approval) => {
        if (!approval || !currentUser) return false;

        // Overall request status must still be Pending
        if (approval.status?.toLowerCase() !== 'pending') return false;

        // Super admins can approve/reject any pending request as an override
        if (currentUser.role === 'super_admin') return true;

        const myId = String(currentUser.id || currentUser._id || '');
        if (!myId) return false;

        // Parallel model: find the current user's OWN flow step and check if it is still Pending
        const myStep = approval.approverFlow?.find(step => {
            const stepApproverId = typeof step.approver === 'object'
                ? String(step.approver._id || step.approver.id || '')
                : String(step.approver || '');
            return stepApproverId === myId;
        });

        return !!myStep && myStep.status?.toLowerCase() === 'pending';
    };

    // Approve / Reject Submission
    const handleReviewSubmit = (action) => {
        if (action === 'Rejected' && !reviewRemarks.trim()) {
            toast.error('Remarks are required when rejecting a request');
            return;
        }

        submitDecisionMutation.mutate({
            id: selectedApprovalId,
            action: action === 'Approved' ? 'approve' : 'reject',
            remarks: reviewRemarks.trim()
        });
    };

    // Styling Helpers
    const getStatusStyle = (status) => {
        switch (status?.toLowerCase()) {
            case 'pending': return 'bg-amber-50 text-amber-700 border border-amber-200';
            case 'approved': return 'bg-green-50 text-green-700 border border-green-200';
            case 'rejected': case 'disapproved': return 'bg-red-50 text-red-700 border border-red-200';
            default: return 'bg-gray-50 text-gray-700';
        }
    };

    const getFlowStepIcon = (status) => {
        switch (status?.toLowerCase()) {
            case 'approved': return <CheckCircle className="h-5 w-5 text-green-600 bg-white rounded-full" />;
            case 'rejected': case 'disapproved': return <XCircle className="h-5 w-5 text-red-600 bg-white rounded-full" />;
            case 'pending': return <Clock className="h-5 w-5 text-amber-500 bg-white rounded-full animate-pulse" />;
            default: return <div className="h-5 w-5 rounded-full bg-gray-200 border-2 border-white" />;
        }
    };

    return (
        <div className="flex flex-col min-h-screen bg-gray-50/50 p-4 md:p-6 lg:p-8 space-y-6">

            {/* Page Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                <div>
                    <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 tracking-tight">
                        Internal Approval Management
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Track requests and perform multi-stage manager reviews on document and purchase requisitions.
                    </p>
                </div>
                {currentUser?.role !== 'super_admin' && (
                    <Button
                        variant="primary"
                        onClick={handleOpenCreateModal}
                        className="bg-agrosaf-green hover:bg-agrosaf-darkGreen text-white px-5 py-2.5 rounded-lg flex items-center space-x-2 shadow-sm"
                        icon={<Plus className="h-4.5 w-4.5" />}
                    >
                        Raise Request
                    </Button>
                )}
            </div>

            {/* Navigation Tabs */}
            <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8">
                    {isSuperAdmin ? (
                        <button className="pb-4 px-1 border-b-2 border-agrosaf-green text-agrosaf-green font-semibold text-sm cursor-default">
                            All Approvals
                        </button>
                    ) : (
                        <>
                            <button
                                onClick={() => { setActiveTab('inbox'); setSelectedApprovalId(null); }}
                                className={`pb-4 px-1 border-b-2 font-semibold text-sm transition-colors ${activeTab === 'inbox'
                                    ? 'border-agrosaf-green text-agrosaf-green'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                Approval Inbox
                            </button>
                            <button
                                onClick={() => { setActiveTab('sent'); setSelectedApprovalId(null); }}
                                className={`pb-4 px-1 border-b-2 font-semibold text-sm transition-colors ${activeTab === 'sent'
                                    ? 'border-agrosaf-green text-agrosaf-green'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                Sent Requests
                            </button>
                        </>
                    )}
                </nav>
            </div>

            {/* KPI Cards — clickable to filter by status */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 mt-4 font-sans">
                <button
                    type="button"
                    onClick={() => setStatusFilter('all')}
                    className={`bg-white p-4 rounded-xl border shadow-sm flex items-center justify-between hover:shadow-md transition-all text-left w-full ${
                        statusFilter === 'all' ? 'border-blue-400 ring-2 ring-blue-200' : 'border-gray-200/80'
                    }`}
                >
                    <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                            {isSuperAdmin
                                ? 'Total Raised Requests'
                                : activeTab === 'inbox'
                                ? 'Total Inbox Requests'
                                : 'Total Sent Requests'}
                        </p>
                        <p className="text-2xl font-bold text-blue-500 mt-1">
                            {isSuperAdmin
                                ? (approvalKPIs?.outbox?.total || 0)
                                : activeTab === 'inbox'
                                ? (approvalKPIs?.inbox?.total || 0)
                                : (approvalKPIs?.outbox?.total || 0)}
                        </p>
                        {statusFilter === 'all' && <p className="text-[10px] text-blue-400 mt-0.5 font-medium">● Active filter</p>}
                    </div>
                    <div className="bg-blue-50 p-3 rounded-lg">
                        <MessageSquare className="h-6 w-6 text-blue-500" />
                    </div>
                </button>
                <button
                    type="button"
                    onClick={() => setStatusFilter('Pending')}
                    className={`bg-white p-4 rounded-xl border shadow-sm flex items-center justify-between hover:shadow-md transition-all text-left w-full ${
                        statusFilter === 'Pending' ? 'border-amber-400 ring-2 ring-amber-200' : 'border-gray-200/80'
                    }`}
                >
                    <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                            {isSuperAdmin
                                ? 'Total Pending Actions'
                                : activeTab === 'inbox'
                                ? 'Pending My Action'
                                : 'Pending Approvals'}
                        </p>
                        <p className="text-2xl font-bold text-amber-500 mt-1">
                            {isSuperAdmin
                                ? (approvalKPIs?.inbox?.pending || 0)
                                : activeTab === 'inbox'
                                ? (approvalKPIs?.inbox?.pending || 0)
                                : (approvalKPIs?.outbox?.pending || 0)}
                        </p>
                        {statusFilter === 'Pending' && <p className="text-[10px] text-amber-400 mt-0.5 font-medium">● Active filter</p>}
                    </div>
                    <div className="bg-amber-50 p-3 rounded-lg">
                        <Clock className="h-6 w-6 text-amber-500" />
                    </div>
                </button>
                <button
                    type="button"
                    onClick={() => setStatusFilter('Approved')}
                    className={`bg-white p-4 rounded-xl border shadow-sm flex items-center justify-between hover:shadow-md transition-all text-left w-full ${
                        statusFilter === 'Approved' ? 'border-green-400 ring-2 ring-green-200' : 'border-gray-200/80'
                    }`}
                >
                    <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Approved</p>
                        <p className="text-2xl font-bold text-green-600 mt-1">
                            {isSuperAdmin
                                ? (approvalKPIs?.outbox?.approved || 0)
                                : activeTab === 'inbox'
                                ? (approvalKPIs?.inbox?.approved || 0)
                                : (approvalKPIs?.outbox?.approved || 0)}
                        </p>
                        {statusFilter === 'Approved' && <p className="text-[10px] text-green-400 mt-0.5 font-medium">● Active filter</p>}
                    </div>
                    <div className="bg-green-50 p-3 rounded-lg">
                        <CheckCircle className="h-6 w-6 text-green-500" />
                    </div>
                </button>
                <button
                    type="button"
                    onClick={() => setStatusFilter('Rejected')}
                    className={`bg-white p-4 rounded-xl border shadow-sm flex items-center justify-between hover:shadow-md transition-all text-left w-full ${
                        statusFilter === 'Rejected' ? 'border-red-400 ring-2 ring-red-200' : 'border-gray-200/80'
                    }`}
                >
                    <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Rejected</p>
                        <p className="text-2xl font-bold text-red-600 mt-1">
                            {isSuperAdmin
                                ? (approvalKPIs?.outbox?.rejected || 0)
                                : activeTab === 'inbox'
                                ? (approvalKPIs?.inbox?.rejected || 0)
                                : (approvalKPIs?.outbox?.rejected || 0)}
                        </p>
                        {statusFilter === 'Rejected' && <p className="text-[10px] text-red-400 mt-0.5 font-medium">● Active filter</p>}
                    </div>
                    <div className="bg-red-50 p-3 rounded-lg">
                        <XCircle className="h-6 w-6 text-red-500" />
                    </div>
                </button>
            </div>

            {/* Filters Row */}
            <div className="bg-white p-4 rounded-xl border border-gray-200/80 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="relative flex-1 w-full md:max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search by request ID, title..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                    />
                </div>
                <div className="flex flex-wrap w-full md:w-auto items-center gap-3">
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 font-medium">From:</span>
                        <input
                            type="date"
                            value={startDate}
                            max={endDate || todayStr}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent text-gray-700 bg-white"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 font-medium">To:</span>
                        <input
                            type="date"
                            value={endDate}
                            min={startDate || undefined}
                            max={todayStr}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent text-gray-700 bg-white"
                        />
                    </div>
                    <select
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value)}
                        className="w-full md:w-auto px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent bg-white text-gray-700"
                    >
                        <option value="all">All Categories</option>
                        {approvalCategories.map(c => (
                            <option key={c._id} value={c.name}>{c.name}</option>
                        ))}
                    </select>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="w-full md:w-auto px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent bg-white text-gray-700"
                    >
                        <option value="all">All Statuses</option>
                        <option value="Pending">Pending</option>
                        <option value="Approved">Approved</option>
                        <option value="Rejected">Rejected</option>
                    </select>
                    {(searchQuery || categoryFilter !== 'all' || statusFilter !== 'all' || startDate || endDate) && (
                        <button
                            type="button"
                            onClick={() => {
                                setSearchQuery('');
                                setCategoryFilter('all');
                                setStatusFilter('all');
                                setStartDate('');
                                setEndDate('');
                            }}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 rounded-lg text-xs font-semibold transition-colors"
                        >
                            <X className="h-3.5 w-3.5" />
                            Clear Filters
                        </button>
                    )}
                </div>
            </div>

            {/* Main Work Area */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                {/* Left/Main Column - Requests List */}
                <div className={`lg:col-span-1 space-y-4 ${selectedApprovalId ? 'hidden lg:block' : 'block'}`}>

                    {/* List */}
                    <div className="space-y-2.5">
                        {isLoading ? (
                            <div className="text-center py-12 bg-white rounded-xl border border-gray-200 shadow-sm">
                                <Clock className="h-8 w-8 text-gray-400 animate-spin mx-auto mb-2" />
                                <p className="text-sm text-gray-500">Loading approvals...</p>
                            </div>
                        ) : filteredApprovals.length > 0 ? (
                            filteredApprovals.map(approval => {
                                const initiatorName = approval.requester ? `${approval.requester.firstName} ${approval.requester.lastName || ''}`.trim() : 'Unknown';
                                return (
                                    <div
                                        key={approval._id}
                                        onClick={() => setSelectedApprovalId(approval._id)}
                                        className={`bg-white p-3.5 rounded-xl border transition-all cursor-pointer hover:shadow-md ${selectedApprovalId === approval._id
                                            ? 'border-agrosaf-green ring-1 ring-agrosaf-green/30 shadow-sm'
                                            : 'border-gray-200 hover:border-gray-300 shadow-sm'
                                            }`}
                                    >
                                        <div className="flex justify-between items-start mb-1.5">
                                            <div className="flex items-center space-x-1.5">
                                                <span className="text-[10px] font-mono font-bold bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded">
                                                    {approval.approvalId}
                                                </span>
                                                <span className="text-[10px] text-gray-400">•</span>
                                                <span className="text-[10px] text-gray-500 font-medium">
                                                    {approval.category}
                                                </span>
                                            </div>
                                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${getStatusStyle(approval.status)}`}>
                                                {approval.status}
                                            </span>
                                        </div>
                                        <h3 className="text-sm font-bold text-gray-950 mb-0.5 line-clamp-1 capitalize">
                                            {approval.title}
                                        </h3>
                                        <p className="text-xs text-gray-500 line-clamp-1 mb-2 first-letter:capitalize">
                                            {approval.description}
                                        </p>
                                        <div className="flex items-center justify-between pt-2 border-t border-gray-100/70 text-[10px] text-gray-400">
                                            <div className="flex items-center space-x-1">
                                                <span className="text-gray-400 mr-0.5">Initiator:</span>
                                                <span className="font-semibold text-gray-600">{initiatorName}</span>
                                            </div>
                                            <span>
                                                Raised {new Date(approval.createdAt).toLocaleDateString()}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center shadow-sm">
                                <Clock className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                                <h3 className="text-lg font-bold text-gray-900 mb-1">No Approval Requests</h3>
                                <p className="text-gray-500 text-sm max-w-sm mx-auto">
                                    No approval entries match the active tab and filters.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between border-t border-gray-100 bg-white px-4 py-3 rounded-xl shadow-sm mt-4">
                            <div className="flex flex-1 justify-between sm:hidden">
                                <button
                                    onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                                    disabled={page === 1}
                                    className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                >
                                    Previous
                                </button>
                                <button
                                    onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
                                    disabled={page === totalPages}
                                    className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                >
                                    Next
                                </button>
                            </div>
                            <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                                <div>
                                    <p className="text-xs text-gray-500">
                                        Showing <span className="font-semibold">{Math.min((page - 1) * limit + 1, totalApprovals)}</span> to{' '}
                                        <span className="font-semibold">{Math.min(page * limit, totalApprovals)}</span> of{' '}
                                        <span className="font-semibold">{totalApprovals}</span> requests
                                    </p>
                                </div>
                                <div>
                                    <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                                        <button
                                            onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                                            disabled={page === 1}
                                            className="relative inline-flex items-center rounded-l-md px-2 py-1.5 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                                        >
                                            <span className="sr-only">Previous</span>
                                            <ChevronRight className="h-4 w-4 rotate-180" />
                                        </button>
                                        <span className="relative inline-flex items-center px-3 py-1.5 text-xs font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 bg-white">
                                            Page {page} of {totalPages}
                                        </span>
                                        <button
                                            onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
                                            disabled={page === totalPages}
                                            className="relative inline-flex items-center rounded-r-md px-2 py-1.5 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                                        >
                                            <span className="sr-only">Next</span>
                                            <ChevronRight className="h-4 w-4" />
                                        </button>
                                    </nav>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Right Column - Detail Workspace */}
                <div className={`lg:col-span-2 ${selectedApprovalId ? 'block' : 'hidden lg:block lg:opacity-50'}`}>

                    {detailsLoading ? (
                        <div className="text-center py-24 bg-white border border-gray-200 rounded-2xl shadow-sm">
                            <Clock className="h-10 w-10 text-gray-400 animate-spin mx-auto mb-3" />
                            <h3 className="font-bold text-gray-900">Loading details...</h3>
                        </div>
                    ) : selectedApproval ? (
                        <div className="bg-white rounded-xl border border-gray-200/80 shadow-sm p-6 space-y-6 flex flex-col w-full min-h-[600px]">
                            {/* Consolidated Details Header */}
                            <div className="flex items-center justify-between pb-4 border-b border-gray-100">
                                <div className="flex items-center space-x-2">
                                    <button
                                        onClick={() => setSelectedApprovalId(null)}
                                        className="lg:hidden p-1 text-gray-500 hover:text-gray-700 -ml-1"
                                    >
                                        <ArrowLeft className="h-5 w-5" />
                                    </button>
                                    <span className="text-sm font-mono font-bold bg-gray-100 text-gray-700 px-3 py-1 rounded">
                                        {selectedApproval.approvalId}
                                    </span>
                                </div>
                                <span className={`text-xs px-2.5 py-0.5 rounded-full font-semibold ${getStatusStyle(selectedApproval.status)}`}>
                                    {selectedApproval.status}
                                </span>
                            </div>

                            {/* Inner Grid for Details & Workflow Timeline */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {/* Left Side (span 1) - Workflow Timeline Stepper */}
                                <div className="md:col-span-1 border-b md:border-b-0 md:border-r border-gray-150 pb-5 md:pb-0 md:pr-6 space-y-4">
                                    <h3 className="font-bold text-gray-900 pb-2 border-b border-gray-100">Approval Workflow</h3>

                                    <div className="relative pl-6 space-y-6 before:absolute before:left-2 before:top-2 before:bottom-2 before:w-0.5 before:bg-gray-100">
                                        {selectedApproval.approverFlow?.map((step, idx) => {
                                            const stepApproverName = step.approver ? `${step.approver.firstName} ${step.approver.lastName || ''}`.trim() : 'Unknown';
                                            return (
                                                <div key={step._id || idx} className="relative flex flex-col items-start w-full min-w-0">

                                                    {/* Icon */}
                                                    <div className="absolute -left-[26px] top-0 flex items-center justify-center">
                                                        {getFlowStepIcon(step.status)}
                                                    </div>

                                                    <div className="ml-2 w-full min-w-0">
                                                        <div className="flex flex-wrap items-center space-x-1.5 gap-y-1">
                                                            <span className="text-sm font-bold text-gray-950 truncate max-w-[150px]" title={stepApproverName}>{stepApproverName}</span>
                                                        </div>
                                                        <p className="text-xs text-gray-500 mt-1 flex items-center gap-1.5">
                                                            Status: 
                                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize border ${getStatusStyle(step.status)}`}>
                                                                {step.status}
                                                            </span>
                                                        </p>

                                                        {step.remarks && (
                                                            <p className="text-xs text-gray-600 italic mt-1.5 bg-gray-50 border border-gray-100 p-2 rounded-lg break-words overflow-hidden whitespace-pre-wrap max-w-full">
                                                                &ldquo;{step.remarks}&rdquo;
                                                            </p>
                                                        )}

                                                        {step.decidedAt && (
                                                            <span className="block text-[10px] text-gray-400 mt-1.5">
                                                                {new Date(step.decidedAt).toLocaleString()}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Right Side (span 2) - Description, Attachments, Actions */}
                                <div className="md:col-span-2 space-y-5 md:pl-6">
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider">Subject</label>
                                        <h2 className="text-lg font-bold text-gray-950 mt-0.5 capitalize">{selectedApproval.title}</h2>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider">Category</label>
                                            <p className="text-sm text-gray-900 font-medium mt-0.5">{selectedApproval.category}</p>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider">Raised By</label>
                                            <p className="text-sm text-gray-900 font-medium mt-0.5">
                                                {selectedApproval.requester ? `${selectedApproval.requester.firstName} ${selectedApproval.requester.lastName || ''}`.trim() : 'Unknown'}
                                            </p>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider">Request Details</label>
                                        <p className="text-sm text-gray-700 mt-1.5 whitespace-pre-wrap leading-relaxed break-words overflow-hidden first-letter:capitalize">
                                            {selectedApproval.description}
                                        </p>
                                    </div>

                                    {/* Action Box for pending approver */}
                                    {isCurrentUserPendingApprover(selectedApproval) && (
                                        <div className="mt-4 bg-green-50/40 border border-green-200 p-4 rounded-xl transition-all">
                                            <h4 className="text-sm font-bold text-gray-950 mb-3">Review Action Required</h4>

                                            <div className="space-y-3">
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                                                        Review Remarks <span className="text-red-500">* (Mandatory for rejection)</span>
                                                    </label>
                                                    <textarea
                                                        placeholder="Provide approval recommendation or rejection reasons..."
                                                        value={reviewRemarks}
                                                        onChange={(e) => setReviewRemarks(e.target.value)}
                                                        rows={3}
                                                        maxLength={500}
                                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent bg-white"
                                                    />
                                                    <p className="text-[10px] text-gray-400 text-right mt-1">
                                                        {reviewRemarks.length}/500 characters
                                                    </p>
                                                </div>
                                                <div className="flex space-x-3">
                                                    <button
                                                        onClick={() => handleReviewSubmit('Approved')}
                                                        disabled={submitDecisionMutation.isPending}
                                                        className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg shadow-sm text-sm transition-all disabled:opacity-50"
                                                    >
                                                        Approve Request
                                                    </button>
                                                    <button
                                                        onClick={() => handleReviewSubmit('Rejected')}
                                                        disabled={submitDecisionMutation.isPending}
                                                        className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg shadow-sm text-sm transition-all disabled:opacity-50"
                                                    >
                                                        Reject
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Attachments */}
                                    {selectedApproval.attachments?.length > 0 && (
                                        <div className="pt-4 border-t border-gray-100">
                                            <label className="block text-xs font-semibold text-gray-400 uppercase mb-2">Attachments</label>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                {selectedApproval.attachments.map((file, i) => (
                                                    <a
                                                        key={i}
                                                        href={file.fileUrl}
                                                        onClick={(e) => handleDownloadAttachment(e, file.fileUrl, file.fileName)}
                                                        className="flex items-center justify-between p-2.5 bg-gray-50 border border-gray-200 rounded-lg text-xs hover:bg-gray-100 hover:border-agrosaf-green transition-colors group"
                                                    >
                                                        <div className="flex items-center space-x-2 truncate">
                                                            <FileText className="h-4 w-4 text-gray-400 flex-shrink-0 group-hover:text-agrosaf-green transition-colors" />
                                                            <span className="text-gray-700 font-medium truncate group-hover:text-agrosaf-green transition-colors">{file.fileName}</span>
                                                        </div>
                                                        <Download className="h-4 w-4 text-agrosaf-green flex-shrink-0" />
                                                    </a>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="hidden lg:flex h-[400px] border border-dashed border-gray-300 bg-white rounded-2xl items-center justify-center text-center p-8 shadow-sm">
                            <div>
                                <Clock className="h-10 w-10 text-gray-300 mx-auto mb-2.5" />
                                <h3 className="font-bold text-gray-900 mb-1">No Approval Selected</h3>
                                <p className="text-sm text-gray-500 max-w-xs mx-auto">
                                    Select a request to inspect approval workflows, current pendency levels, and action logs.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Create Approval Request Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 bg-black/55 transition-opacity" onClick={() => setShowCreateModal(false)}></div>

                        {/* Center modal */}
                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

                        <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-xl sm:w-full border border-gray-100 p-6 space-y-6">

                            <div className="flex items-center justify-between pb-4 border-b border-gray-100">
                                <h3 className="text-lg font-extrabold text-gray-950">Raise Approval Request</h3>
                                <button
                                    onClick={() => setShowCreateModal(false)}
                                    className="text-gray-400 hover:text-gray-600 p-1"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>

                            <form onSubmit={handleCreateApproval} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                                        Subject / Title <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={newApprovalData.title}
                                        onChange={(e) => handleFieldChange('title', e.target.value)}
                                        onBlur={() => handleFieldBlur('title')}
                                        maxLength={100}
                                        className={`w-full px-3.5 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent text-sm transition-colors ${
                                            formTouched.title && formErrors.title
                                                ? 'border-red-500 focus:ring-red-500/20'
                                                : 'border-gray-300 focus:ring-agrosaf-green/30'
                                        }`}
                                        placeholder="Brief summary of what needs approval..."
                                    />
                                    {formTouched.title && formErrors.title && (
                                        <p className="mt-1 text-xs text-red-500 font-medium">{formErrors.title}</p>
                                    )}
                                    <p className="mt-0.5 text-[10px] text-gray-400 text-right">
                                        {newApprovalData.title.length}/100 characters
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                                        Category <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={newApprovalData.category}
                                        onChange={(e) => handleFieldChange('category', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent text-sm bg-white"
                                    >
                                        {approvalCategories.map(c => (
                                            <option key={c._id} value={c.name}>{c.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                                        Select Approvers <span className="text-red-500">*</span>
                                        <span className="text-xs font-normal text-gray-400 ml-1">(Any one approval will finalise the request)</span>
                                    </label>
                                    <div className="flex items-center gap-2 mb-2">
                                        <SearchableDropdown
                                            options={employeesList
                                                .filter(emp => emp.userId && String(emp.userId) !== String(currentUser?.id || currentUser?._id) && !newApprovalData.approverIds.includes(String(emp.userId)))
                                                .map(emp => {
                                                    const dept = emp.department && emp.department !== 'N/A' ? emp.department : '';
                                                    const pos = emp.position && emp.position !== 'N/A' ? emp.position : '';
                                                    const role = emp.role
                                                        ? emp.role.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
                                                        : 'Employee';
                                                    const details = [dept, pos].filter(Boolean).join(' - ') || role;
                                                    return {
                                                        id: emp.userId,
                                                        displayText: `${emp.name} (${details})`,
                                                        title: emp.name,
                                                        _empData: emp
                                                    };
                                                })}
                                            value={null}
                                            onChange={(val) => {
                                                if (!val) return;
                                                // Find the employee from the current list
                                                const emp = employeesList.find(e => String(e.userId) === String(val));
                                                if (!emp) return;
                                                const dept = emp.department && emp.department !== 'N/A' ? emp.department : '';
                                                const pos = emp.position && emp.position !== 'N/A' ? emp.position : '';
                                                const role = emp.role
                                                    ? emp.role.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
                                                    : 'Employee';
                                                const formattedDept = [dept, pos].filter(Boolean).join(' - ') || role;

                                                const updatedApproverIds = [...newApprovalData.approverIds, String(val)];
                                                const updatedApproverNames = [...(newApprovalData.approverNames || []), {
                                                    userId: String(val),
                                                    name: emp.name || 'Unknown',
                                                    department: formattedDept
                                                }];

                                                const updatedData = {
                                                    ...newApprovalData,
                                                    approverIds: updatedApproverIds,
                                                    approverNames: updatedApproverNames
                                                };
                                                setNewApprovalData(updatedData);
                                                setApproverSearch('');
                                                toast.success(`${emp.name} added as approver`);

                                                // Update validation errors real-time
                                                const errors = validateCreateForm(updatedData);
                                                setFormErrors(errors);
                                            }}
                                            onSearch={handleAppSearch}
                                            onScrollToBottom={handleAppScrollToBottom}
                                            hasMore={hasMoreApp}
                                            isLoadingMore={loadingMoreApp}
                                            loading={loadingApp}
                                            placeholder="-- Select an Approver --"
                                            searchPlaceholder="Search approvers..."
                                            className="flex-1 min-w-0"
                                        />
                                    </div>

                                    {/* Approver workflow display */}
                                    {newApprovalData.approverIds.length > 0 ? (
                                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2">
                                            {newApprovalData.approverIds.map((userId, index) => {
                                                const approverInfo = (newApprovalData.approverNames || []).find(a => a.userId === userId)
                                                    || employeesList.find(e => String(e.userId) === userId);
                                                return (
                                                    <div key={userId} className="flex items-center justify-between bg-white border border-gray-150 p-2 rounded-md text-xs gap-2">
                                                        <div className="flex items-center space-x-2 min-w-0 flex-1">
                                                            <span className="font-bold text-gray-700 flex-shrink-0">{index + 1}.</span>
                                                            <span className="font-medium text-gray-900 truncate">{approverInfo?.name}</span>
                                                            <span className="text-gray-400 truncate">({approverInfo?.department})</span>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const updatedApproverIds = newApprovalData.approverIds.filter(val => val !== userId);
                                                                const updatedApproverNames = (newApprovalData.approverNames || []).filter(a => a.userId !== userId);
                                                                const updatedData = {
                                                                    ...newApprovalData,
                                                                    approverIds: updatedApproverIds,
                                                                    approverNames: updatedApproverNames
                                                                };
                                                                setNewApprovalData(updatedData);
                                                                
                                                                // Update validation errors real-time
                                                                const errors = validateCreateForm(updatedData);
                                                                setFormErrors(errors);
                                                            }}
                                                            className="text-red-500 hover:text-red-700 flex-shrink-0 ml-2"
                                                        >
                                                            Remove
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <p className="text-xs text-gray-500 italic">No approvers added yet. Sequential order will follow level indicators.</p>
                                    )}
                                    {formTouched.approvers && formErrors.approvers && (
                                        <p className="mt-1 text-xs text-red-500 font-medium">{formErrors.approvers}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                                        Description <span className="text-red-500">*</span>
                                    </label>
                                    <textarea
                                        value={newApprovalData.description}
                                        onChange={(e) => handleFieldChange('description', e.target.value)}
                                        onBlur={() => handleFieldBlur('description')}
                                        rows={4}
                                        maxLength={1000}
                                        className={`w-full px-3.5 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent text-sm transition-colors ${
                                            formTouched.description && formErrors.description
                                                ? 'border-red-500 focus:ring-red-500/20'
                                                : 'border-gray-300 focus:ring-agrosaf-green/30'
                                        }`}
                                        placeholder="Detail the justification for this request..."
                                    />
                                    {formTouched.description && formErrors.description && (
                                        <p className="mt-1 text-xs text-red-500 font-medium">{formErrors.description}</p>
                                    )}
                                    <p className="mt-0.5 text-[10px] text-gray-400 text-right">
                                        {newApprovalData.description.length}/1000 characters
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                                        Supporting Documents
                                    </label>
                                    <div 
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onDrop={handleDrop}
                                        className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-lg transition-colors ${
                                            uploadingFiles ? 'border-agrosaf-green bg-green-50/30' : 
                                            isDragging ? 'border-agrosaf-green bg-green-50/20' : 'border-gray-300'
                                        }`}
                                    >
                                        <div className="space-y-1 text-center">
                                            {uploadingFiles ? (
                                                <>
                                                    <Clock className="mx-auto h-10 w-10 text-agrosaf-green animate-spin" />
                                                    <p className="text-sm text-agrosaf-green font-medium">Uploading to cloud...</p>
                                                </>
                                            ) : (
                                                <>
                                                    <Paperclip className="mx-auto h-10 w-10 text-gray-400" />
                                                    <div className="flex justify-center text-sm text-gray-600">
                                                        <label className="relative cursor-pointer bg-white rounded-md font-medium text-agrosaf-green hover:text-agrosaf-darkGreen focus-within:outline-none">
                                                            <span>Upload files</span>
                                                            <input
                                                                type="file"
                                                                multiple
                                                                accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.txt,.rtf"
                                                                disabled={uploadingFiles}
                                                                onChange={handleFileAttach}
                                                                className="hidden"
                                                            />
                                                        </label>
                                                        <p className="pl-1">or drag and drop</p>
                                                    </div>
                                                    <p className="text-xs text-center text-gray-500">
                                                        PDF, DOCX, XLSX, images up to 25MB each
                                                    </p>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    {/* Uploaded Files Feedback */}
                                    {newApprovalData.attachments.length > 0 && (
                                        <div className="flex flex-wrap gap-1.5 mt-3">
                                            {newApprovalData.attachments.map((f, i) => (
                                                <span key={i} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-200">
                                                    {f.fileName || f}
                                                    <button
                                                        type="button"
                                                        onClick={() => setNewApprovalData(prev => ({
                                                            ...prev,
                                                            attachments: prev.attachments.filter((_, idx) => idx !== i)
                                                        }))}
                                                        className="ml-1 text-green-500 hover:text-green-700"
                                                    >
                                                        <X className="h-3 w-3" />
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => setShowCreateModal(false)}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        disabled={createApprovalMutation.isPending}
                                        className="bg-agrosaf-green hover:bg-agrosaf-darkGreen text-white disabled:opacity-50"
                                    >
                                        Raise Request
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InternalApprovals;
